import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { RefreshCw, CheckCircle2, Lock, Loader2, Copy, AlertTriangle, Eye, EyeOff, ShieldCheck } from 'lucide-react';

const API = axios.create({ baseURL: '/api/v1' });
API.interceptors.request.use((cfg) => {
  const token = sessionStorage.getItem('portal_token');
  if (token) cfg.headers.Authorization = `Bearer ${token}`;
  return cfg;
});

interface CredStatus {
  uat: { issued: boolean; issuedAt: string | null; apiKey: string | null };
  prod: { issued: boolean; issuedAt: string | null; gated: boolean; gateReasons: string[] };
}
interface IssuedCreds { env: string; apiKey: string; apiSecret: string; issuedAt: string }

function CopyButton({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* */ }
      }}
      className="inline-flex items-center gap-1 rounded-lg border border-gray-200 px-2 py-1 text-xs hover:bg-gray-50"
    >
      {copied ? <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" aria-hidden /> : <Copy className="h-3.5 w-3.5" aria-hidden />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

export const PartnerCredentialsCard: React.FC<{ clientId: number }> = ({ clientId }) => {
  const [status, setStatus] = useState<CredStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<IssuedCreds | null>(null);
  const [showSecret, setShowSecret] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await API.get(`/disbursement-clients/${clientId}/credentials`);
      setStatus(data.credentials);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load credentials');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [clientId]);

  const call = async (path: string) => {
    setBusy(true);
    setError(null);
    try {
      const { data } = await API.post(`/disbursement-clients/${clientId}/credentials/${path}`);
      if (data.credentials?.apiSecret) {
        setRevealed(data.credentials);
        setShowSecret(true);
      }
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Action failed');
    } finally {
      setBusy(false);
    }
  };

  // Authorise the existing credential pair for production use. This does NOT
  // regenerate the secret — the same pair the partner already holds is
  // authorised for the production base URL. Gated by the backend on UAT
  // sign-off + float prefunding; the button is additionally disabled until
  // both gates are clear (prod.gated === false) and not already authorised.
  const authoriseProd = async () => {
    if (busy || !status?.prod || status.prod.issued || status.prod.gated) return;
    if (!confirm(
      'Authorise production API credentials for this partner?\n\n' +
      'This enables LIVE money movement against the production base URL. ' +
      'Only do this after UAT sign-off AND production float prefunding are complete.',
    )) return;
    await call('prod/issue');
  };

  return (
    <div className="mymoolah-card overflow-hidden">
      <div className="p-5 border-b border-gray-100 flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="admin-text-heading text-lg">API Credentials</h3>
          <p className="text-xs text-gray-400 mt-0.5">UAT reveal-once + rotate. Production gated until UAT sign-off + float prefund; authorise production after both gates pass.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => call('uat/issue')} disabled={busy || status?.uat.issued}
            className="px-3 py-2 rounded-xl text-xs font-semibold bg-[var(--primary)] text-[var(--primary-foreground)] disabled:opacity-50">
            Issue UAT
          </button>
          <button onClick={() => call('uat/rotate')} disabled={busy}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-semibold border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <RefreshCw className="h-3.5 w-3.5" aria-hidden />}
            Rotate UAT
          </button>
          <button onClick={() => call('uat/signoff')} disabled={busy || !status?.uat.issued || !status?.prod.gated}
            className="px-3 py-2 rounded-xl text-xs font-semibold border border-gray-200 hover:bg-gray-50 disabled:opacity-50">
            Mark UAT sign-off
          </button>
          <button onClick={authoriseProd} disabled={busy || !status?.prod || status.prod.issued || status.prod.gated}
            className="inline-flex items-center gap-1 px-3 py-2 rounded-xl text-xs font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : <ShieldCheck className="h-3.5 w-3.5" aria-hidden />}
            Authorise Production
          </button>
        </div>
      </div>
      <div className="p-5">
        {loading && <div className="flex items-center gap-2 text-sm text-gray-500"><Loader2 className="h-4 w-4 animate-spin" aria-hidden /> Loading…</div>}
        {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
        {status && !loading && (
          <div className="space-y-3">
            <div className="rounded-xl border border-gray-100 p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-800">UAT (Sandbox)</p>
                {status.uat.issued ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-800">
                    <CheckCircle2 className="h-3.5 w-3.5" aria-hidden /> Issued
                  </span>
                ) : <span className="text-xs text-gray-400">Not issued</span>}
              </div>
              {status.uat.issued && status.uat.apiKey && (
                <div className="mt-2 flex items-center justify-between gap-2">
                  <code className="font-mono text-xs text-gray-700 truncate">{status.uat.apiKey}</code>
                  <CopyButton value={status.uat.apiKey} />
                </div>
              )}
            </div>
            <div className="rounded-xl border border-gray-100 p-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-gray-800">Production</p>
                {status.prod.issued ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-medium text-emerald-800">
                    <ShieldCheck className="h-3.5 w-3.5" aria-hidden /> Authorised
                  </span>
                ) : status.prod.gated ? (
                  <span className="inline-flex items-center gap-1 rounded-full bg-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700">
                    <Lock className="h-3.5 w-3.5" aria-hidden /> Locked
                  </span>
                ) : (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-xs font-medium text-amber-800">
                    <AlertTriangle className="h-3.5 w-3.5" aria-hidden /> Ready to authorise
                  </span>
                )}
              </div>
              {status.prod.issued && status.prod.issuedAt && (
                <p className="mt-1 text-xs text-gray-500">
                  Authorised {new Date(status.prod.issuedAt).toLocaleString()}
                </p>
              )}
              {status.prod.gated && status.prod.gateReasons.map((r) => (
                <p key={r} className="mt-1 flex items-start gap-1 text-xs text-gray-500">
                  <AlertTriangle className="mt-0.5 h-3 w-3 text-amber-500" aria-hidden /> {r}
                </p>
              ))}
              {!status.prod.issued && !status.prod.gated && (
                <p className="mt-1 text-xs text-gray-500">
                  UAT sign-off and float prefunding complete. Click <em>Authorise Production</em> above to enable live credentials.
                </p>
              )}
            </div>
          </div>
        )}
      </div>

      {revealed && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl">
            <div className="flex items-start gap-3">
              <div className="rounded-lg bg-amber-100 p-2"><AlertTriangle className="h-5 w-5 text-amber-600" aria-hidden /></div>
              <div className="flex-1">
                <h3 className="text-sm font-semibold text-gray-900">UAT credentials — save the secret now</h3>
                <p className="mt-1 text-xs text-gray-500">The API secret is shown only once. Relay it to the partner securely; it cannot be retrieved again.</p>
              </div>
            </div>
            <div className="mt-4 space-y-3">
              <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
                <p className="text-xs font-medium uppercase text-gray-500">API Key</p>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <code className="truncate font-mono text-sm">{revealed.apiKey}</code>
                  <CopyButton value={revealed.apiKey} />
                </div>
              </div>
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3">
                <p className="text-xs font-medium uppercase text-amber-700">API Secret (shown once)</p>
                <div className="mt-1 flex items-center justify-between gap-2">
                  <code className="truncate font-mono text-sm">{showSecret ? revealed.apiSecret : '•••••••••••••••••••••••••••'}</code>
                  <div className="flex items-center gap-1">
                    <button type="button" onClick={() => setShowSecret((s) => !s)} className="rounded-lg border border-gray-200 p-1.5 hover:bg-gray-50" aria-label="Toggle secret">
                      {showSecret ? <EyeOff className="h-3.5 w-3.5" aria-hidden /> : <Eye className="h-3.5 w-3.5" aria-hidden />}
                    </button>
                    <CopyButton value={revealed.apiSecret} />
                  </div>
                </div>
              </div>
            </div>
            <button type="button" onClick={() => { setRevealed(null); setShowSecret(false); }}
              className="mt-4 w-full rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-semibold text-[var(--primary-foreground)] hover:opacity-90">
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
